import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircleIcon, ExclamationTriangleIcon, LockClosedIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import preopService from '../../services/preopService';
import Logo from '../../components/Logo';

const REFUSED = {
  invalid: ['This link isn’t valid', 'Please check that you opened the complete link from your email or message.'],
  expired: ['This link has expired', 'Please contact the practice — they can send you a new link, and anything you saved is kept.'],
  cancelled: ['This questionnaire is no longer needed', 'Please contact the practice if you think this is a mistake.'],
  superseded: ['There is a newer link', 'The practice has sent you a newer link — please use that one.'],
  completed: ['You have already sent your answers', 'Thank you. If anything has changed since, please let the practice know.'],
  no_practice: ['This page isn’t linked to a practice', 'Please open the link exactly as you received it.'],
};
const YES_NO = [['yes', 'Yes'], ['no', 'No']];
const RELATIONSHIPS = [['self', 'I am the patient'], ['parent', 'I am their parent'], ['guardian', 'I am their legal guardian'], ['other', 'I am helping the patient']];
const SAVE_IDLE_MS = 8000;

// Mirrors backend/src/preop/form.js — the server checks everything again.
export const shown = (q, answers) => !q.when || q.when.in.includes(answers?.[q.when.q]?.value);
const detailOpens = (q, value) => (q.detailOn || ['yes']).includes(value);
const isRequired = (q) => (q.type === 'yesno' || q.type === 'choice' ? !q.optional : !!q.required);
const isOptional = (q) => !isRequired(q);
const answered = (q, a) => {
  if (q.type === 'yesno' || q.type === 'choice') return !!a?.value;
  if (q.type === 'list') return Array.isArray(a) && a.some((row) => String(row?.[q.fields[0].id] || '').trim());
  return a != null && String(a).trim() !== '';
};
const displayValue = (q, a) => {
  if (q.type === 'yesno') return { yes: 'Yes', no: 'No', unsure: 'Not sure' }[a.value];
  if (q.type === 'choice') return q.options.find((o) => o.value === a.value)?.label || a.value;
  if (q.type === 'number') return `${a}${q.unit ? ` ${q.unit}` : ''}`;
  if (q.type === 'list') return a.filter((row) => String(row?.[q.fields[0].id] || '').trim()).map((row) => q.fields.map((f) => row[f.id]).filter(Boolean).join(' — ')).join('; ');
  return String(a);
};
const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const inputCls = 'w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30';

const Choice = ({ on, onClick, children, wide }) => (
  <button type="button" onClick={onClick} className={`${wide ? 'w-full text-left' : 'min-w-[5.5rem] flex-1 text-center'} rounded-lg border-2 px-4 py-3 text-base font-semibold transition ${on ? 'border-brand-teal bg-teal-50 text-brand-deep' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'}`}>
    {children}
  </button>
);

// One question, in whichever shape it takes. Also used by the question editor's preview.
export const Question = ({ q, value, onChange, missing }) => {
  const withValue = (v) => onChange(v === value?.value ? value : { value: v, detail: value?.detail });
  return (
    <div id={`q-${q.id}`} className={`rounded-xl border bg-white p-4 ${missing ? 'border-red-400 ring-2 ring-red-100' : 'border-slate-200'}`}>
      <p className="font-semibold text-slate-800">
        {q.label}
        {isOptional(q) && <span className="ml-1 text-xs font-normal text-slate-400">(optional)</span>}
      </p>
      {q.help && <p className="mt-1 text-sm text-slate-500">{q.help}</p>}

      <div className="mt-3">
        {q.type === 'yesno' && (
          <div className="flex flex-wrap gap-2">
            {[...YES_NO, ...(q.allowUnsure ? [['unsure', 'Not sure']] : [])].map(([v, label]) => (
              <Choice key={v} on={value?.value === v} onClick={() => withValue(v)}>{label}</Choice>
            ))}
          </div>
        )}
        {q.type === 'choice' && (
          <div className="space-y-2">
            {q.options.map((o) => <Choice key={o.value} wide on={value?.value === o.value} onClick={() => withValue(o.value)}>{o.label}</Choice>)}
          </div>
        )}
        {(q.type === 'yesno' || q.type === 'choice') && value?.value && detailOpens(q, value.value) && (
          <label className="mt-3 block text-sm text-slate-600">{q.detailLabel || 'Tell us more'} <span className="text-xs text-slate-400">(optional)</span>
            <textarea rows={2} value={value.detail || ''} onChange={(e) => onChange({ ...value, detail: e.target.value })} placeholder={q.detailPlaceholder || ''} maxLength={500} className={`mt-1 resize-none ${inputCls}`} />
          </label>
        )}
        {q.type === 'number' && (
          <div className="flex max-w-[12rem] items-center gap-2">
            <input type="number" inputMode="decimal" aria-label={q.label} min={q.min} max={q.max} value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)} className={inputCls} />
            {q.unit && <span className="text-slate-500">{q.unit}</span>}
          </div>
        )}
        {q.type === 'text' && (
          <textarea rows={3} aria-label={q.label} value={value || ''} onChange={(e) => onChange(e.target.value || undefined)} placeholder={q.placeholder || ''} maxLength={1000} className={`resize-none ${inputCls}`} />
        )}
        {q.type === 'list' && (() => {
          const rows = Array.isArray(value) && value.length ? value : [{}];
          const setRow = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
          return (
            <div className="space-y-2">
              {rows.map((row, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
                    {q.fields.map((f) => (
                      <input key={f.id} aria-label={f.label} value={row[f.id] || ''} onChange={(e) => setRow(i, { [f.id]: e.target.value })} placeholder={f.placeholder || f.label} maxLength={150} className={inputCls} />
                    ))}
                  </div>
                  {rows.length > 1 && (
                    <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} title="Remove" className="mt-2 rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-red-600"><XMarkIcon className="h-5 w-5" /></button>
                  )}
                </div>
              ))}
              {rows.length < 30 && (
                <button type="button" onClick={() => onChange([...rows, {}])} className="inline-flex items-center gap-1 text-sm font-semibold text-brand-teal"><PlusIcon className="h-4 w-4" /> {q.addLabel || 'Add another'}</button>
              )}
            </div>
          );
        })()}
      </div>
      {missing && <p className="mt-2 text-sm text-red-600">Please answer this question.</p>}
    </div>
  );
};

/**
 * PublicPreop — the page a patient opens from the practice's link: health
 * questions for their anaesthetist, one section at a time. Answers save as they
 * go (they can close the page and come back); at the end they check their
 * answers, confirm the declaration and send them. No login.
 */
const PublicPreop = () => {
  const { token } = useParams();
  const [phase, setPhase] = useState('loading'); // loading | intro | form | review | done | refused
  const [data, setData] = useState(null);
  const [refused, setRefused] = useState(null);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [missing, setMissing] = useState(new Set());
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('self');
  const [agree, setAgree] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const answersRef = useRef({});
  const dirty = useRef(false);
  const timer = useRef(null);

  const refuse = (d) => { setRefused(d); setPhase('refused'); };

  useEffect(() => {
    preopService.publicLoad(token)
      .then((d) => {
        setData(d);
        setAnswers(d.answers || {});
        answersRef.current = d.answers || {};
        if (d.questionnaire.is_minor) setRelationship('parent');
        setPhase('intro');
      })
      .catch((e) => {
        const d = e.response?.data;
        refuse({ ...d, code: d?.code || 'invalid', network: !e.response });
      });
  }, [token]);

  const save = useCallback(async () => {
    clearTimeout(timer.current);
    if (!dirty.current) return;
    dirty.current = false;
    setSaveState('saving');
    try {
      await preopService.publicSave(token, answersRef.current);
      setSaveState('saved');
    } catch (e) {
      const d = e.response?.data;
      if (d?.code && REFUSED[d.code]) { refuse(d); return; }
      dirty.current = true;
      setSaveState('error');
    }
  }, [token]);

  // Save when the patient pauses, and when they switch away (phones rarely fire unload).
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') save(); };
    document.addEventListener('visibilitychange', onHide);
    return () => { document.removeEventListener('visibilitychange', onHide); clearTimeout(timer.current); };
  }, [save]);

  const setAnswer = (id, value) => {
    setAnswers((a) => {
      const next = { ...a };
      if (value === undefined) delete next[id]; else next[id] = value;
      answersRef.current = next;
      return next;
    });
    dirty.current = true;
    setSaveState('idle');
    clearTimeout(timer.current);
    timer.current = setTimeout(save, SAVE_IDLE_MS);
    if (missing.has(id)) setMissing((m) => { const n = new Set(m); n.delete(id); return n; });
  };

  const sections = data?.form || [];
  const goTo = (i, nextPhase = 'form') => {
    save();
    setStep(i);
    setMissing(new Set());
    setError('');
    setPhase(nextPhase);
    window.scrollTo({ top: 0 });
  };

  const next = () => {
    const s = sections[step];
    const miss = s.questions.filter((q) => shown(q, answers) && isRequired(q) && !answered(q, answers[q.id])).map((q) => q.id);
    if (miss.length) {
      setMissing(new Set(miss));
      setError(`Please answer ${miss.length === 1 ? 'the question' : `the ${miss.length} questions`} marked in red.`);
      document.getElementById(`q-${miss[0]}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    goTo(step + 1, step === sections.length - 1 ? 'review' : 'form');
  };

  const submit = async () => {
    setSending(true);
    setError('');
    clearTimeout(timer.current);
    try {
      await preopService.publicComplete(token, { answers, full_name: name, relationship, agree });
      dirty.current = false;
      setPhase('done');
      window.scrollTo({ top: 0 });
    } catch (e) {
      const d = e.response?.data;
      if (d?.missing?.length) {
        const idx = sections.findIndex((s) => s.id === d.missing[0].section);
        setMissing(new Set(d.missing.map((m) => m.id)));
        if (idx >= 0) { setStep(idx); setPhase('form'); window.scrollTo({ top: 0 }); }
        setError(d.err);
      } else if (d?.code && REFUSED[d.code]) {
        refuse(d);
      } else {
        setError(d?.err || (e.response ? 'Something went wrong — please try again.' : 'You seem to be offline — check your connection and try again.'));
      }
    } finally {
      setSending(false);
    }
  };

  const practice = data?.practice || refused?.practice;
  const info = data?.questionnaire;
  const section = sections[step];
  const minor = info?.is_minor;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 py-3">
          <div className="min-w-0">
            <div className="truncate font-bold text-brand-deep">{practice?.name || 'Your anaesthetist’s practice'}</div>
            <div className="text-xs text-slate-500">Health questions before your anaesthetic</div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            {phase === 'form' && (saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : saveState === 'error' ? <span className="text-amber-600">Not saved yet</span> : null)}
            <LockClosedIcon className="h-5 w-5" title="Secure link" />
          </div>
        </div>
        {phase === 'form' && (
          <div className="h-1 bg-slate-100"><div className="h-1 bg-brand-teal transition-all" style={{ width: `${((step + 1) / (sections.length + 1)) * 100}%` }} /></div>
        )}
      </header>

      <main className="mx-auto max-w-2xl px-4 py-6">
        {phase === 'loading' && <p className="py-16 text-center text-slate-400">Opening your questions…</p>}

        {phase === 'refused' && (
          <div className="flex flex-col items-center rounded-xl border border-slate-200 bg-white px-6 py-10 text-center">
            {refused.code === 'completed' ? <CheckCircleIcon className="h-12 w-12 text-green-600" /> : <ExclamationTriangleIcon className="h-12 w-12 text-amber-500" />}
            <h1 className="mt-3 text-xl font-bold text-brand-deep">{refused.network ? 'We couldn’t reach the practice' : (REFUSED[refused.code] || REFUSED.invalid)[0]}</h1>
            <p className="mt-2 max-w-md text-sm text-slate-600">{refused.network ? 'Please check your internet connection and open the link again.' : (REFUSED[refused.code] || REFUSED.invalid)[1]}</p>
            {practice?.phone && <p className="mt-4 text-sm text-slate-600">Call {practice.name || 'the practice'} on <a href={`tel:${practice.phone}`} className="font-semibold text-brand-teal">{practice.phone}</a></p>}
          </div>
        )}

        {phase === 'intro' && info && (
          <div className="space-y-4">
            <div>
              <h1 className="text-xl font-bold text-brand-deep">Hello {info.first_names},</h1>
              <p className="mt-1 text-slate-600">
                Before your procedure, your anaesthetist would like to know about {minor ? 'the patient’s' : 'your'} health, so they can plan a safe anaesthetic.
                It takes about 10 minutes. Your answers are saved as you go — you can close this page and open the link again later.
              </p>
              {minor && <p className="mt-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-900">If you are a parent or guardian filling this in, please answer every question about the patient.</p>}
            </div>
            <section className="rounded-xl border border-slate-200 bg-white p-4">
              <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                <div><dt className="text-xs text-slate-500">Date</dt><dd className="font-semibold">{fmtDate(info.service_date)}</dd></div>
                <div><dt className="text-xs text-slate-500">Anaesthetist</dt><dd className="font-semibold">{info.doctor}</dd></div>
                {info.hospital_name && <div><dt className="text-xs text-slate-500">Hospital</dt><dd className="font-semibold">{info.hospital_name}</dd></div>}
                {info.surgeon && <div><dt className="text-xs text-slate-500">Surgeon</dt><dd className="font-semibold">{info.surgeon}</dd></div>}
                {info.procedure_text && <div className="sm:col-span-2"><dt className="text-xs text-slate-500">Procedure</dt><dd className="font-semibold">{info.procedure_text}</dd></div>}
              </dl>
            </section>
            <p className="text-sm text-slate-500">Your answers go only to your anaesthetist and their practice, and are kept confidential.</p>
            <button onClick={() => goTo(0)} className="w-full rounded-lg bg-brand-gradient px-4 py-3.5 text-base font-semibold text-white">
              {info.last_saved_at ? 'Carry on where you left off' : 'Start'}
            </button>
          </div>
        )}

        {phase === 'form' && section && (
          <div className="space-y-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Step {step + 1} of {sections.length}</div>
              <h1 className="text-xl font-bold text-brand-deep">{section.title}</h1>
              {section.intro && <p className="mt-1 text-sm text-slate-600">{section.intro}</p>}
            </div>
            {section.questions.filter((q) => shown(q, answers)).map((q) => (
              <Question key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} missing={missing.has(q.id)} />
            ))}
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex gap-2 pt-1">
              <button onClick={() => (step === 0 ? goTo(0, 'intro') : goTo(step - 1))} className="rounded-lg border border-slate-300 bg-white px-4 py-3 text-base font-semibold text-slate-700">Back</button>
              <button onClick={next} className="flex-1 rounded-lg bg-brand-gradient px-4 py-3 text-base font-semibold text-white">
                {step === sections.length - 1 ? 'Check my answers' : 'Next'}
              </button>
            </div>
          </div>
        )}

        {phase === 'review' && (
          <div className="space-y-4">
            <div>
              <h1 className="text-xl font-bold text-brand-deep">Check your answers</h1>
              <p className="mt-1 text-sm text-slate-600">Tap <b>Change</b> to fix anything, then confirm and send them at the bottom.</p>
            </div>
            {sections.map((s, i) => {
              const items = s.questions.filter((q) => shown(q, answers) && answered(q, answers[q.id]));
              return (
                <section key={s.id} className="rounded-xl border border-slate-200 bg-white">
                  <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
                    <h2 className="font-bold text-slate-700">{s.title}</h2>
                    <button onClick={() => goTo(i)} className="text-sm font-semibold text-brand-teal">Change</button>
                  </div>
                  <dl className="divide-y divide-slate-100 text-sm">
                    {items.map((q) => (
                      <div key={q.id} className="px-4 py-2">
                        <dt className="text-slate-500">{q.label}</dt>
                        <dd className="font-semibold text-slate-800">{displayValue(q, answers[q.id])}{answers[q.id]?.detail ? <span className="block font-normal text-slate-600">{answers[q.id].detail}</span> : null}</dd>
                      </div>
                    ))}
                    {!items.length && <p className="px-4 py-2 text-slate-400">Nothing entered.</p>}
                  </dl>
                </section>
              );
            })}

            <section className="space-y-3 rounded-xl border-2 border-brand-teal/50 bg-white p-4">
              <p className="font-semibold text-slate-700">Who is filling this in?</p>
              <div className="space-y-2">
                {RELATIONSHIPS.map(([v, label]) => <Choice key={v} wide on={relationship === v} onClick={() => setRelationship(v)}>{label}</Choice>)}
              </div>
              <label className="block text-sm font-semibold text-slate-700">Your full name
                <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" className={`mt-1 ${inputCls}`} />
              </label>
              <label className="flex items-start gap-3 text-sm text-slate-700">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 h-5 w-5 rounded border-slate-300" />
                <span>{data.consent_text}</span>
              </label>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button onClick={submit} disabled={sending || !agree || name.trim().length < 3} className="w-full rounded-lg bg-brand-gradient px-4 py-3.5 text-base font-semibold text-white disabled:opacity-50">
                {sending ? 'Sending…' : 'Send my answers'}
              </button>
            </section>
          </div>
        )}

        {phase === 'done' && (
          <div className="flex flex-col items-center rounded-xl border border-slate-200 bg-white px-6 py-10 text-center">
            <CheckCircleIcon className="h-12 w-12 text-green-600" />
            <h1 className="mt-3 text-xl font-bold text-brand-deep">Thank you — your answers have been sent</h1>
            <div className="mt-2 max-w-md space-y-2 text-sm text-slate-600">
              <p>Your anaesthetist will read them before your procedure and may contact you if they have questions.</p>
              <p>The hospital or practice will tell you when to stop eating and drinking. If anything changes before the day — for example you get a cold or start a new medicine — please let the practice know.</p>
            </div>
            {practice?.phone && <p className="mt-4 text-sm text-slate-600">Call {practice.name || 'the practice'} on <a href={`tel:${practice.phone}`} className="font-semibold text-brand-teal">{practice.phone}</a></p>}
          </div>
        )}

        <p className="mt-8 flex items-center justify-center gap-1.5 text-xs text-slate-400">
          <Logo variant="icon" className="h-4 w-4" /> Powered by OmniPrac
        </p>
      </main>
    </div>
  );
};

export default PublicPreop;
